<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  ApplicationEvents,
  CaseListService,
  CaseReevaluationService,
  Contexts,
  DataSynchService,
  getContext,
  NamedBindings,
  VariableCaseListColumn,
  getContainer,
  TranslationService,
  CachedCaseListElement,
  type CaseListActionParams,
  CaseDeletionFailed,
  CasePermanentlyDeleted,
  CaseDeleted,
  NewCaseButtonComponent,
  StatusCaseListColumn,
  CRFVersionSelectionStrategyInterface,
  CRFServiceInterface,
  CRFToHTMLService,
  CRF,
  ExportPanel,
  LoggerInterface,
  ContainerTypes,
  CaseServiceInterface,
  CaseListServiceInterface,
  CaseForUpdate,
  UpdateCaseEvent,
  EventsStorageInterface,
  CaseErrorType,
  CaseError,
  CaseStorageInterface,
  PartialStoredCase,
  DataSynchStorageInterface,
  OpenCaseRequested,
  OpenDeletionDialogRequested
} from "@dandelion/core";
import SearchFilterComponent from "./SearchFilterComponent.vue";
import sodium from 'libsodium-wrappers';
import ImporterComponent from "./ImporterComponent.vue";
import router from "../router";
import { Messages } from "../enum/Messages";
import { UserService } from "../services/UserService";
import YearSelectionComponent from "./YearSelectionComponent.vue";
import { CaseListActionInterface } from "../interfaces/CaseListActionInterface";
import ConsentDialogComponent from "./Dialogs/ConsentDialogComponent.vue";
import CaseListComponent from "./CaseList/CaseListComponent.vue";
import DeletedCaseListComponent from "./CaseList/DeletedCaseListComponent.vue"
import { NamedServices } from "../enum/NamedServices";
import { PreferencesService } from "../services/PreferencesService";
import { Preferences } from "../enum/Preferences";
import { ImporterMode } from "../enum/ImporterMode";
import { Users } from "../enum/Users";
import { ReadmissionStorageInterface } from "../interfaces/ReadmissionStorageInterface";
import { cloneDeep } from "lodash";
import { ReadmissionManagerRunner } from "../services/ReadmissionManagerRunner";
import ReadmissionHashChange from "../entities/ReadmissionHashChange";
import { MagicStrings } from "../MagicStrings";
import RegistryForHash from "../interfaces/Registry";
import { RESTStorageForEvents } from "@dandelion/core/storages";
import { toast } from "vue3-toastify";


let newCaseRequestedToken: string;
let caseDeletedToken: string;
let casePermanentlyDeletedToken: string;
let caseDeletionFailedToken: string;
let showSearch = ref(false);

const currentActiveCRFs = await (
  getContainer().get(
    NamedBindings.CRFVersionSelectionStrategy
  ) as CRFVersionSelectionStrategyInterface
).getCRFConfigurationsByCentreCode();
const keyForDeletedRender = ref(1);
const caseListColumns = [
  new VariableCaseListColumn('Admission Key', 'admissionKey'),
  new VariableCaseListColumn('Name', 'core.personalData.name'),
  new VariableCaseListColumn('Surname', 'core.personalData.surname'),
  new VariableCaseListColumn('Birth Date', 'core.personalData.birthDate'),
  new VariableCaseListColumn(
    'ICU Admission Date',
    'core.administrativeInfo.icuAdmDate'
  ),
  new VariableCaseListColumn("ehrIdAdmission", "core.administrativeInfo.ehrIdAdmission"),
  new VariableCaseListColumn("Consent","consent"),
  ...currentActiveCRFs.map(
    (status) =>
      new StatusCaseListColumn(status.crfName, status.crfName, '*')
  ),
];

const container = getContainer();
const caseListService = container.get(NamedBindings.CaseListService) as CaseListServiceInterface;

const totalCaseCount = computed(() => caseListService.allCases.length);
const caseCountForPage = computed(() => caseListService.slicedCases.length);
const ts = container.get(
  NamedBindings.TranslationService
) as TranslationService;
const userService = getContainer(Contexts.SHARED).get(
  NamedBindings.UserService
) as UserService;
const centrePreferencesService = container.get(
  NamedServices.CentrePreferencesService
) as PreferencesService;
const isImporterAutomatic =
  (await centrePreferencesService.getPreference(Preferences.TYPEIMPORT))
    ?.value == ImporterMode.AUTOMATIC;
const shouldAskForConsent =
  (await centrePreferencesService.getPreference(Preferences.ASKCONSENT))
    .value !== 'false';
const logger = container.get(NamedBindings.Logger) as LoggerInterface;

let selectedCaseListElement = ref<CaseListActionParams>();
const additionalActionForCaseList: Array<CaseListActionInterface> = [
  
  {
    name: 'Manage Consent',
    actionClass: 'manage-consent',
    action: async (values: CaseListActionParams) => {
      selectedCaseListElement.value = values;
      await nextTick();
      const openDialogBtn = document.getElementById(
        'open-consent-choice-dialog-btn'
      );
      openDialogBtn?.click();
    },
    isVisible: (_values: CaseListActionParams) => {
      if (
        userService.getCurrentUser().value?.role !== Users.ADMIN &&
        userService.getCurrentUser().value?.role !== Users.WRITER
      ) {
        return false;
      }
      return shouldAskForConsent;
    },
  },
  {
    name: 'Show report',
    actionClass: 'show-report',
    action: async (values: CaseListActionParams) => {
      router.push('/report/' + values.element.caseMetaData.caseID.toString());
    },
    isVisible: (_values: CaseListActionParams) => {
      if (_values.element.caseMetaData.status.get("*").get("core") != "0") {
        return true;
      }
      return false;
    },
    tooltip: ts.t("Show report"),
    icon: "document"
    
  },
  {
    name: 'Copy CaseID',
    actionClass: 'copy-caseid',
    action: async(values: CaseListActionParams)=>{
       const caseId = values.element.caseMetaData.caseID.toString();
      try {
    await navigator.clipboard.writeText(caseId);
    toast.success('Chiave di Ammissione copiata!');
  } catch (e) {
    toast.error('Error while copying to clipboard:');
  }
    },
  tooltip: ts.t("Copy CaseID"),
  icon: "copy"
  }
];

const searchref = ref<InstanceType<typeof SearchFilterComponent> | null>(null)


function cleanSearchFilters() {
  searchref.value?.cleanFilters()
}

async function synchronize() {
  const synchService = getContainer('contextForSynch').get(
    NamedBindings.DataSynchService
  ) as DataSynchService;
  await synchService.synchronize();
}

function closeSearch() {
  showSearch.value = false;
}

const isLoading = ref(true);
function stopLoading() {
  isLoading.value = false;
}

onMounted(() => {
  // Simula il termine del caricamento
  setTimeout(() => {
    stopLoading(); // Nasconde lo spinner quando il caricamento è completato
  }, 2000); // Puoi sostituire con la logica effettiva del caricamento
});

onMounted(() => {
  newCaseRequestedToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.NewCaseRequested,
    async () => {
      await router.push('new-patient');
    }
  );
  caseDeletedToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseDeleted,
    async (_m: string, e: Event) => {
      const event: CaseDeleted = e as CaseDeleted;
      await removeFromReadmissionsChains(event.caseID); // TODO: move in "before" hook like permanently
      logger.logSuccess(
        ts.t(Messages.CASE_DELETED) + event.caseID?.substring(0, 14),
        {}
      );
    }
  );

  casePermanentlyDeletedToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CasePermanentlyDeleted,
    async (_m: string, e: Event) => {
      const event: CasePermanentlyDeleted = e as CasePermanentlyDeleted;
      logger.logSuccess(
        ts.t(Messages.CASE_PERMANENTLY_DELETED) +
          event.caseID?.substring(0, 14),
        {}
      );
    }
  );

  caseDeletionFailedToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseDeletionFailed,
    async (_m: string, e: Event) => {
      const event: CaseDeletionFailed = e as CaseDeletionFailed;
      logger.logError(
        ts.t(Messages.CASE_DELETION_FAILED) + event.caseID?.substring(0, 14),
        {}
      );
    }
  );
});

onBeforeUnmount(() => {
  getContext(Contexts.DEFAULT).removeEventListenerByToken(
    newCaseRequestedToken
  );
  getContext(Contexts.DEFAULT).removeEventListenerByToken(caseDeletedToken);
  getContext(Contexts.DEFAULT).removeEventListenerByToken(
    casePermanentlyDeletedToken
  );
  getContext(Contexts.DEFAULT).removeEventListenerByToken(
    caseDeletionFailedToken
  );
});

watch(showSearch, () => {
  const searchDialog = document.getElementById(
    'search-dialog'
  ) as HTMLDialogElement;
  showSearch.value ? searchDialog.showModal() : searchDialog.close();
});

getContext(Contexts.DEFAULT).addEventListener(
  ApplicationEvents.CaseDeleted,
  () => keyForDeletedRender.value++
);

getContext(Contexts.DEFAULT).addEventListener(
  ApplicationEvents.CaseRestored,
  () => keyForDeletedRender.value++
);

getContext(Contexts.DEFAULT).addEventListener(
  ApplicationEvents.CasePermanentlyDeleted,
  () => keyForDeletedRender.value++
);

getContext(Contexts.DEFAULT).addEventListener(
  ApplicationEvents.CaseOpenedForEditing,
  () => {
    const spinnerLoading = document.getElementById('spinner-loading');
    if (spinnerLoading) spinnerLoading.style.display = 'block';
  }
);

async function removeFromReadmissionsChains(caseID: string) {
  const caseService = container.get(
    NamedBindings.CaseService
  ) as CaseServiceInterface;
  const { data } = await caseService.getDataFor(caseID);
  const { centreCode } = await caseService.getCaseMetaDataFor(caseID);
  const readmissionStorage = container.get(
    'ReadmissionStorage'
  ) as ReadmissionStorageInterface;
  const readmissionHashChange = new ReadmissionHashChange(
    caseService,
    readmissionStorage
  );
  await readmissionHashChange.removeCaseFromChains(caseID, data, centreCode);
}

async function reevaluate() {
  const reevaluationService = getContext(Contexts.DEFAULT).container.get(
    NamedBindings.CaseReevaluationService
  ) as CaseReevaluationService;
  const listService = getContext(Contexts.DEFAULT).container.get(
    NamedBindings.CaseListService
  ) as CaseListService;
  const list: Readonly<Array<CachedCaseListElement>> = listService.allCases;
  await reevaluationService.run(Array.from(list));
}

async function precacheCrfs() {
  // Add here the list of CRFs that you want to be pre-cached at start (versions specified in the variable below)
  // This means more time to load the application, but less time when we open the fist admission
  const crfNamesToBeCached = [
    'core' /*,
    'infections',
    'cch',
    'colon',
    'livertransplant',
    'start',
    
    'ich',
    'esa',
    'covid',
    'trauma'
    */,
  ].filter((crfName) =>
    currentActiveCRFs.find(activeCrf => crfName == activeCrf.crfName)
  );

  // Here you should speciy, for each of the pre-cached CRFs, which version you want to be cached
  // (generally, it should be the last available)
  const crfVersionsToBeCached = { default: '2.0.0' } as const;

  const xslPathsAndPrefixes = [
    { path: 'xslt-overrides/Schemas/SchemaToCollectionPage.xsl', prefix: '' },
    { path: 'xslt-overrides/Schemas/SchemaToTree.xsl', prefix: 'tree-' },
    { path: 'xslt/Schemas/SchemaToWarningList.xsl', prefix: 'wList-' },
    { path: 'xslt/Schemas/SchemaToErrorList.xsl', prefix: 'eList-' },
  ] as const;

  const crfService = getContainer(Contexts.SHARED).get(
    NamedBindings.CRFService
  ) as CRFServiceInterface;
  for (const crfName of crfNamesToBeCached) {
    const version = Object.keys(crfVersionsToBeCached).includes(crfName)
      ? crfVersionsToBeCached[crfName as keyof typeof crfVersionsToBeCached]
      : crfVersionsToBeCached.default;
    const crf = await crfService.getCRF(crfName, version);
    for (const pp of xslPathsAndPrefixes) {
      generateCacheForCRF(crf, pp.path, pp.prefix);
    }
    if (crfName == 'core') {
      // We generate cache also for NewCase
      generateCacheForCRF(
        crf,
        `xslt-overrides/Schemas/SchemaToCollectionPage.xsl`,
        '',
        'NewCase'
      );
    }
  }

  function generateCacheForCRF(
    crf: CRF,
    xslPath: string,
    prefix: string,
    startingDataClass = ''
  ) {
    const crfToHTML = getContainer(Contexts.SHARED).get(
      NamedBindings.CRFToHTMLService
    ) as CRFToHTMLService;
    // We simply call the transformation, the service will automatically cache it
    crfToHTML.getHTMLfromXML(
      crf.xml,
      crf.name,
      crf.version,
      `${import.meta.env.BASE_URL}${xslPath}`,
      true,
      true,
      true,
      // @ts-ignore
      15,
      '',
      `model.${crf.name}`,
      prefix
    );
  }
}

await precacheCrfs();
</script>

<template>
  <dialog id="search-dialog" class="modal-dialog">
    <ion-icon
      id="close-icon"
      name="close-outline"
      @click="showSearch = false"
      class="absolute right-2 top-2"
    ></ion-icon>
    <SearchFilterComponent ref="searchref" @closeSearch="closeSearch" />
  </dialog>

  <main id="clc" class="flex-1 overflow-y-auto md:pt-4 pt-4 px-6">
    <Suspense>
      <YearSelectionComponent />
    </Suspense>
    <div class="text-2xl w-full text-center inline-block">
      <h1 id="case-list-title" v-html="ts.t('Patient List')"></h1>
    </div>
    <div class="divider mt-2"></div>
    <section id="home-buttons-container">
      <NewCaseButtonComponent />
      <button id="search-button" @click="showSearch = true">
        <span v-html="ts.t('Search')"></span>
        <ion-icon icon="search"></ion-icon>
      </button>
    </section>
      <section>
          <span v-html="ts.t('Cases shown in page: ')" />
          {{ caseCountForPage }} di {{ totalCaseCount  }}
        </section>
    <Suspense>
      <div>
        <div v-if="isLoading">
          <img
            src="/images/loading.gif"
            alt="Caricamento dei casi"
            class="big-spinner"
            style="display: none"
            id="spinner-loading"
          />
        </div>
        <CaseListComponent
          :number-of-elements-per-page="50"
          :case-list-columns="caseListColumns"
          :key="keyForDeletedRender"
          :add-default-actions="true"
          :additional-actions="additionalActionForCaseList"
          :openOnLineClick="true"
          @cleanfilters="cleanSearchFilters"
        />
      </div>
    </Suspense>
    <Suspense>
      <DeletedCaseListComponent
        :case-list-columns="caseListColumns"
        :key="keyForDeletedRender"
      />
    </Suspense>
    <section id="utils-buttons">
      <ExportPanel />
      <button
        v-if="userService.getCurrentUser()?.value?.role === Users.ADMIN"
        @click="synchronize()"
        v-html="ts.t('Synchronize')"
      ></button>        
      <!-- <button @click="reevaluate()" v-html="ts.t('Run Reevaluation')"></button>
      <FastComponent></FastComponent>-->
    </section>
    <Suspense
      v-if="
        !isImporterAutomatic &&
        (userService.getCurrentUser()?.value?.role === Users.ADMIN ||
          userService.getCurrentUser()?.value?.role === Users.WRITER)
      "
    >
      <ImporterComponent />
    </Suspense>
    <ConsentDialogComponent
      :case-list-element="selectedCaseListElement?.element"
    />
  </main>
</template>

<style scoped>
#search-dialog {
  padding: 2rem 5rem 1rem 2rem;
}

#search-dialog #close-icon {
  font-size: 2rem;
  padding: 0.25rem;
  border-radius: 50%;
  background-color: #388087;
  color: #fff;
  margin: 0.25rem;
  cursor: pointer;
}

#search-button ion-icon {
  min-width: 1.25rem;
  min-height: 1.25rem;
}

#home-buttons-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 0.5rem;
}

#clc {
  padding: 1rem;
}

#case-list-title {
  color: #388087;
  font-weight: 700;
}
</style>
